/**
 * Tätigkeits-Kacheln für den Tagesplan (CRM-Tab und öffentlicher Kundenlink).
 *
 * Pro Tätigkeit aus `PB_ACTIVITIES` eine Kachel. Gezogen wird mit
 * `react-draggable` (`DraggableCore`, Maus und Touch): während des Ziehens
 * folgt eine Vorschau-Kachel dem Zeiger, der Elternteil bekommt die
 * Bildschirmkoordinaten (`onTileDrag`/`onTileDrop`) und rechnet daraus die
 * Minute im Zeitraster aus. Am Rand eines Scrollbereichs wird mitgescrollt,
 * solange der Zeiger dort steht (am Handy liegen die Kacheln unter dem Raster).
 * Mit dem Finger beginnt das Ziehen erst nach kurzem Gedrückthalten, vorher
 * scrollt eine Wischbewegung über die Kacheln ganz normal die Seite. Klick,
 * Antippen oder Enter legen die Tätigkeit direkt im Anschluss an den letzten
 * Eintrag an.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { DraggableCore as DraggableCoreRaw, type DraggableCoreProps, type DraggableEvent } from 'react-draggable';
import { Card } from '../../../../components/ui';
import { BRAND, getThemeColors } from '../../../../lib/theme';
import {
  PB_ACTIVITIES,
  activityLabel,
  activityDurationMin,
  isPauseBlock,
} from './scheduleUtils';

// Die mitgelieferten Typen machen über `defaultProps` alle Props zu Pflicht-Props.
const DraggableCore = DraggableCoreRaw as unknown as React.ComponentType<Partial<DraggableCoreProps>>;

export interface TilePoint { x: number; y: number }

interface Props {
  dark: boolean;
  disabled?: boolean;
  /**
   * `grid`: Kacheln umbrechen (Desktop). `strip`: eine Reihe, seitlich
   * wischbar — kompakt genug, um am Handy oben am Bildschirm festzustehen.
   */
  variant?: 'grid' | 'strip';
  /** Klick/Antippen/Enter auf eine Kachel (ohne Ziehen). */
  onPick: (careType: string) => void;
  /** Zieh-Beginn/-Ende — für die Vorschau im Zeitraster. */
  onDragStateChange?: (careType: string | null) => void;
  /** Zeigerposition während des Ziehens (Viewport-Koordinaten). */
  onTileDrag?: (careType: string, point: TilePoint) => void;
  /** Kachel losgelassen (Viewport-Koordinaten). */
  onTileDrop?: (careType: string, point: TilePoint) => void;
}

/** Ab so viel Pixeln Bewegung gilt es als Ziehen, darunter als Klick/Tippen (Finger wackeln mehr). */
const DRAG_THRESHOLD_MOUSE_PX = 5;
const DRAG_THRESHOLD_TOUCH_PX = 10;
/** So lange muss der Finger ruhig auf der Kachel liegen, bevor sie am Finger hängt. */
export const LONG_PRESS_MS = 350;
/** Abstand zum Rand eines Scrollbereichs, ab dem beim Ziehen mitgescrollt wird. */
const AUTOSCROLL_EDGE_PX = 60;
/** Höchste Scroll-Geschwindigkeit (Pixel pro Frame) direkt am Rand. */
const AUTOSCROLL_MAX_PX = 18;

function fmtDuration(min: number): string {
  if (min < 60) return `${min} Min`;
  const h = Math.floor(min / 60);
  const r = min % 60;
  return r === 0 ? `${h} Std` : `${h} Std ${r} Min`;
}

const isTouchEvent = (e: DraggableEvent): e is TouchEvent =>
  !!(e as TouchEvent).changedTouches;

/** Zeigerposition; bei Touch der Finger, mit dem das Ziehen begonnen hat. */
function pointOf(e: DraggableEvent, touchId: number | null = null): TilePoint | null {
  if (isTouchEvent(e)) {
    const list = Array.from(e.changedTouches);
    const t = list.find(x => x.identifier === touchId) ?? list[0];
    return t ? { x: t.clientX, y: t.clientY } : null;
  }
  const me = e as MouseEvent;
  return typeof me.clientX === 'number' ? { x: me.clientX, y: me.clientY } : null;
}

function isScrollable(el: Element): el is HTMLElement {
  const oy = getComputedStyle(el).overflowY;
  return (oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight;
}

/**
 * Scrollbare Vorfahren, innerster zuerst: erst die unter dem Zeiger (CRM:
 * Zeitraster, Tab-Inhalt), dann die der Kachel (Zeiger über Kopfzeile o. ä.),
 * zuletzt das Dokument.
 */
function scrollCandidates(p: TilePoint, tile: HTMLElement | null): HTMLElement[] {
  const out: HTMLElement[] = [];
  const add = (start: Element | null) => {
    for (let n = start; n; n = n.parentElement) if (isScrollable(n) && !out.includes(n)) out.push(n);
  };
  const x = Math.min(Math.max(p.x, 0), window.innerWidth - 1);
  const y = Math.min(Math.max(p.y, 0), window.innerHeight - 1);
  add(document.elementFromPoint?.(x, y) ?? null);
  add(tile?.parentElement ?? null);
  const root = document.scrollingElement as HTMLElement | null;
  if (root && !out.includes(root)) out.push(root);
  return out;
}

/** Einen Schritt mitscrollen; true, wenn sich etwas bewegt hat. */
function autoScrollBy(p: TilePoint, tile: HTMLElement | null): boolean {
  for (const sc of scrollCandidates(p, tile)) {
    const isRoot = sc === document.scrollingElement;
    const r = isRoot ? { top: 0, bottom: window.innerHeight } : sc.getBoundingClientRect();
    const top = Math.max(r.top, 0);
    const bottom = Math.min(r.bottom, window.innerHeight);
    let dy = 0;
    if (p.y < top + AUTOSCROLL_EDGE_PX && sc.scrollTop > 0) {
      dy = -Math.min(AUTOSCROLL_MAX_PX, Math.ceil(((top + AUTOSCROLL_EDGE_PX - p.y) / AUTOSCROLL_EDGE_PX) * AUTOSCROLL_MAX_PX));
    } else if (p.y > bottom - AUTOSCROLL_EDGE_PX && sc.scrollTop + sc.clientHeight < sc.scrollHeight) {
      dy = Math.min(AUTOSCROLL_MAX_PX, Math.ceil(((p.y - (bottom - AUTOSCROLL_EDGE_PX)) / AUTOSCROLL_EDGE_PX) * AUTOSCROLL_MAX_PX));
    }
    if (dy === 0) continue;
    const before = sc.scrollTop;
    sc.scrollTop = before + dy;
    if (sc.scrollTop !== before) return true;
  }
  return false;
}

interface TileProps extends Omit<Props, 'dark'> {
  activity: string;
  tc: ReturnType<typeof getThemeColors>;
  dark: boolean;
}

/** Höhe der festgesetzten Auswahl liegt unter Drawer (1000) und Vorschau-Kachel (10000). */
export const PALETTE_STICKY_Z = 30;

function tileStyle(pause: boolean, dark: boolean, tc: ReturnType<typeof getThemeColors>): React.CSSProperties {
  return {
    display: 'flex', flexDirection: 'column', gap: 2,
    padding: '6px 8px', borderRadius: 8,
    border: `1px solid ${pause ? '#22c55e' : tc.border}`,
    borderLeft: `3px solid ${pause ? '#16a34a' : BRAND}`,
    background: pause
      ? (dark ? 'rgba(34,197,94,0.12)' : 'rgba(34,197,94,0.08)')
      : tc.card,
    userSelect: 'none',
    WebkitUserSelect: 'none',   // iOS Safari: sonst Textauswahl/Lupe beim Gedrückthalten
  };
}

/** Kachel in der einzeiligen Leiste: feste Breite, schrumpft nicht. */
const STRIP_TILE: React.CSSProperties = { flex: '0 0 132px' };

function ActivityTile({ activity: a, tc, dark, disabled = false, variant = 'grid', onPick, onDragStateChange, onTileDrag, onTileDrop }: TileProps) {
  const { t: tr } = useTranslation();
  const nodeRef = useRef<HTMLDivElement>(null);
  const startRef = useRef<TilePoint | null>(null);
  const touchIdRef = useRef<number | null>(null);
  const movedRef = useRef(false);
  /** Berührung/Mausdruck dieser Kachel läuft (Schutz vor Nachzüglern aus DraggableCore). */
  const activeRef = useRef(false);
  /** Touch: Finger hat sich vor Ablauf des Gedrückthaltens bewegt → Scrollen, kein Ziehen. */
  const scrollingRef = useRef(false);
  /** Touch: Kachel wurde nach dem Aufnehmen vom Startpunkt weggezogen (dann nie mehr Antippen). */
  const leftStartRef = useRef(false);
  const pressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressClickRef = useRef(false);
  const lastPointRef = useRef<TilePoint | null>(null);
  const rafRef = useRef<number | null>(null);
  const onTileDragRef = useRef(onTileDrag);
  onTileDragRef.current = onTileDrag;
  const onDragStateChangeRef = useRef(onDragStateChange);
  onDragStateChangeRef.current = onDragStateChange;
  const [ghost, setGhost] = useState<TilePoint | null>(null);
  const pause = isPauseBlock({ care_type: a });
  const label = activityLabel(a, tr);
  const duration = fmtDuration(activityDurationMin(a));

  const stopAutoScroll = () => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  };
  const clearPressTimer = () => {
    if (pressTimerRef.current !== null) clearTimeout(pressTimerRef.current);
    pressTimerRef.current = null;
  };
  /** Alles zurücksetzen; lief ein Ziehen, die Vorschau im Raster beenden. */
  const reset = () => {
    const wasDragging = movedRef.current;
    activeRef.current = false;
    scrollingRef.current = false;
    leftStartRef.current = false;
    movedRef.current = false;
    startRef.current = null;
    touchIdRef.current = null;
    lastPointRef.current = null;
    clearPressTimer();
    stopAutoScroll();
    setGhost(null);
    return wasDragging;
  };
  const resetRef = useRef(reset);
  resetRef.current = reset;
  const clearPressTimerRef = useRef(clearPressTimer);
  clearPressTimerRef.current = clearPressTimer;
  // Verschwindet die Kachel mitten im Ziehen, Vorschau im Raster zurücksetzen.
  useEffect(() => () => {
    clearPressTimer();
    stopAutoScroll();
    if (movedRef.current) onDragStateChangeRef.current?.(null);
  }, []);

  // Hängt die Kachel am Finger, darf die Seite nicht mehr scrollen. touch-action
  // greift nur zu Beginn einer Berührung, daher touchmove direkt abbrechen
  // (React-Listener sind passiv). Hat der Browser schon zu scrollen begonnen,
  // lässt sich das nicht mehr stoppen: dann das Ziehen abbrechen.
  useEffect(() => {
    const node = nodeRef.current;
    if (!node) return undefined;
    const onMove = (e: TouchEvent) => {
      if (!movedRef.current) {
        // Browser scrollt schon (seine Toleranz ist kleiner als unsere Schwelle): kein Ziehen mehr.
        if (activeRef.current && !e.cancelable) {
          scrollingRef.current = true;
          clearPressTimerRef.current();
        }
        return;
      }
      if (e.cancelable) e.preventDefault();
      else if (resetRef.current()) onDragStateChangeRef.current?.(null);
    };
    const onCancel = () => {
      if (activeRef.current && resetRef.current()) onDragStateChangeRef.current?.(null);
    };
    node.addEventListener('touchmove', onMove, { passive: false });
    node.addEventListener('touchcancel', onCancel);
    return () => {
      node.removeEventListener('touchmove', onMove);
      node.removeEventListener('touchcancel', onCancel);
    };
  }, []);

  // Läuft pro Frame, solange der Zeiger am Rand steht — auch ohne Bewegung.
  const autoScrollStep = () => {
    rafRef.current = null;
    const p = lastPointRef.current;
    if (!p || !autoScrollBy(p, nodeRef.current)) return;
    onTileDragRef.current?.(a, p);   // Raster hat sich unter dem Zeiger verschoben
    rafRef.current = requestAnimationFrame(autoScrollStep);
  };

  /** Ziehen beginnt: Vorschau-Kachel an den Zeiger, Raster informieren. */
  const beginDrag = (p: TilePoint) => {
    movedRef.current = true;
    onDragStateChangeRef.current?.(a);
    setGhost(p);
    lastPointRef.current = p;
    onTileDragRef.current?.(a, p);
    if (rafRef.current === null) rafRef.current = requestAnimationFrame(autoScrollStep);
  };

  const handleStart = (e: DraggableEvent) => {
    if (reset()) onDragStateChangeRef.current?.(null);
    activeRef.current = true;
    const touch = isTouchEvent(e);
    touchIdRef.current = touch && e.changedTouches.length > 0 ? e.changedTouches[0].identifier : null;
    startRef.current = pointOf(e, touchIdRef.current);
    if (touch && startRef.current) {
      const s = startRef.current;
      pressTimerRef.current = setTimeout(() => {
        pressTimerRef.current = null;
        if (!activeRef.current || scrollingRef.current || movedRef.current) return;
        // Ohne vorherige Nutzeraktion blockt Chrome vibrate() mit Konsolenwarnung.
        if (navigator.userActivation?.hasBeenActive) navigator.vibrate?.(15);
        beginDrag(lastPointRef.current ?? s);
      }, LONG_PRESS_MS);
    }
  };

  const handleDrag = (e: DraggableEvent) => {
    if (!activeRef.current || scrollingRef.current) return;
    const p = pointOf(e, touchIdRef.current);
    const s = startRef.current;
    if (!p || !s) return;
    if (!movedRef.current) {
      const touch = isTouchEvent(e);
      if (Math.hypot(p.x - s.x, p.y - s.y) < (touch ? DRAG_THRESHOLD_TOUCH_PX : DRAG_THRESHOLD_MOUSE_PX)) {
        lastPointRef.current = p;
        return;
      }
      if (touch) {
        // Vor Ablauf des Gedrückthaltens bewegt: der Browser scrollt, kein Ziehen.
        scrollingRef.current = true;
        clearPressTimer();
        return;
      }
      beginDrag(p);
      return;
    }
    if (Math.hypot(p.x - s.x, p.y - s.y) >= DRAG_THRESHOLD_TOUCH_PX) leftStartRef.current = true;
    setGhost(p);
    lastPointRef.current = p;
    onTileDrag?.(a, p);
    if (rafRef.current === null) rafRef.current = requestAnimationFrame(autoScrollStep);
  };

  const handleStop = (e: DraggableEvent) => {
    if (!activeRef.current) return;
    const p = pointOf(e, touchIdRef.current);
    const s = startRef.current;
    const scrolled = scrollingRef.current;
    const leftStart = leftStartRef.current;
    const moved = reset();
    const touchEnd = e.type === 'touchend';
    // touchstart bleibt offen (Scrollen); den vom Browser nachgeschobenen
    // Klick unterbindet erst das Abbrechen von touchend.
    if (touchEnd && e.cancelable) e.preventDefault();
    // Langes Antippen ohne Bewegung: Kachel hing schon am Finger, gemeint war Antippen.
    // Wer weg- und zurückgezogen hat, bricht ab (wie mit der Maus).
    const heldInPlace = touchEnd && !leftStart && !!p && !!s && Math.hypot(p.x - s.x, p.y - s.y) < DRAG_THRESHOLD_TOUCH_PX;
    if (moved && heldInPlace) {
      onDragStateChange?.(null);
      onPick(a);
    } else if (moved) {
      // Endet das Ziehen über der Kachel selbst, käme danach noch ein Klick.
      suppressClickRef.current = true;
      setTimeout(() => { suppressClickRef.current = false; }, 0);
      if (p) onTileDrop?.(a, p);
      onDragStateChange?.(null);
    } else if (touchEnd && !scrolled) {
      onPick(a);
    }
  };

  return (
    <>
      <DraggableCore
        nodeRef={nodeRef}
        disabled={disabled}
        allowMobileScroll
        onStart={handleStart}
        onDrag={handleDrag}
        onStop={handleStop}
      >
        <div
          ref={nodeRef}
          role="listitem"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled || undefined}
          data-activity={a}
          data-dragging={ghost ? 'true' : undefined}
          title={disabled ? undefined : tr('pflege.schedule.palette.tile_title')}
          onClick={() => {
            if (disabled || suppressClickRef.current) return;
            onPick(a);
          }}
          onContextMenu={e => { if (activeRef.current) e.preventDefault(); }}
          onKeyDown={e => {
            if (disabled) return;
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(a); }
          }}
          style={{
            ...tileStyle(pause, dark, tc),
            ...(variant === 'strip' ? STRIP_TILE : null),
            cursor: disabled ? 'not-allowed' : ghost ? 'grabbing' : 'grab',
            opacity: disabled ? 0.55 : ghost ? 0.5 : 1,
            touchAction: disabled ? undefined : 'manipulation',
            WebkitTouchCallout: 'none',
          }}
        >
          <span style={{ fontSize: 11.5, fontWeight: 700, color: tc.heading, lineHeight: 1.2 }}>{label}</span>
          <span style={{ fontSize: 10.5, color: tc.muted, fontVariantNumeric: 'tabular-nums' }}>{duration}</span>
        </div>
      </DraggableCore>
      {ghost && createPortal(
        <div
          aria-hidden="true"
          data-testid="activity-drag-ghost"
          style={{
            ...tileStyle(pause, dark, tc),
            position: 'fixed', left: ghost.x, top: ghost.y, zIndex: 10000,
            transform: 'translate(-50%, -120%)', pointerEvents: 'none',
            minWidth: 120, boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
          }}
        >
          <span style={{ fontSize: 11.5, fontWeight: 700, color: tc.heading, lineHeight: 1.2 }}>{label}</span>
          <span style={{ fontSize: 10.5, color: tc.muted, fontVariantNumeric: 'tabular-nums' }}>{duration}</span>
        </div>,
        document.body,
      )}
    </>
  );
}

export function ActivityPalette({ dark, disabled = false, variant = 'grid', onPick, onDragStateChange, onTileDrag, onTileDrop }: Props) {
  const tc = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const strip = variant === 'strip';
  const hint = disabled ? tr('pflege.schedule.palette.disabled_hint') : tr('pflege.schedule.palette.hint');

  return (
    // Raster-Variante: Ist die Höhe begrenzt (sticky Palette im CRM), scrollt nur
    // die Kachelliste; Überschrift und Hinweis bleiben stehen.
    <Card dark={dark} style={{ padding: strip ? 10 : 14, display: 'flex', flexDirection: 'column', gap: strip ? 6 : 10, ...(strip ? null : { minHeight: 0, maxHeight: '100%' }) }}>
      <div style={strip ? { display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 } : undefined}>
        <h3 style={{ margin: 0, color: tc.heading, fontSize: 14, fontWeight: 700, whiteSpace: strip ? 'nowrap' : undefined }}>{tr('pflege.schedule.palette.title')}</h3>
        <p
          title={strip ? hint : undefined}
          style={{
            margin: strip ? 0 : '3px 0 0', color: tc.muted, fontSize: strip ? 11 : 12,
            ...(strip ? { flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } : null),
          }}
        >
          {hint}
        </p>
      </div>
      <div
        role="list"
        aria-label={tr('pflege.schedule.palette.title')}
        data-variant={variant}
        style={strip
          ? { display: 'flex', gap: 6, overflowX: 'auto', overflowY: 'hidden', paddingBottom: 2 }
          : { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(128px, 1fr))', gap: 6, minHeight: 0, overflowY: 'auto' }}
      >
        {PB_ACTIVITIES.map(a => (
          <ActivityTile
            key={a}
            activity={a}
            tc={tc}
            dark={dark}
            disabled={disabled}
            variant={variant}
            onPick={onPick}
            onDragStateChange={onDragStateChange}
            onTileDrag={onTileDrag}
            onTileDrop={onTileDrop}
          />
        ))}
      </div>
    </Card>
  );
}

/**
 * Liegt der Punkt (Viewport-Koordinaten) auf dem sichtbaren Teil des Elements?
 * Das Rechteck allein reicht nicht: Im CRM scrollt das Zeitraster in einem
 * eigenen Bereich, weggescrollte Teile liegen dann unter Kennzahlen oder
 * Kopfzeile. Deshalb zusätzlich prüfen, was tatsächlich unter dem Zeiger liegt
 * (die Vorschau-Kachel hat pointer-events: none).
 */
export function pointInElement(el: Element | null, p: TilePoint): boolean {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  if (p.x < r.left || p.x > r.right || p.y < r.top || p.y > r.bottom) return false;
  if (typeof document.elementFromPoint !== 'function') return true;   // jsdom
  const hit = document.elementFromPoint(p.x, p.y);
  return hit !== null && el.contains(hit);
}
